import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Scroll position per history entry (23 Oct 2026). Back / Forward return a
 * list to where it was — its rows load after the page mounts, so the restore
 * waits (up to 4 s) for the page to be tall enough, and stops as soon as the
 * user scrolls. A new page (a link, a menu click) opens at the top.
 */
const KEY = 'scroll-memory';
const read = () => { try { return JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch { return {}; } };
const write = (m) => {
  try {
    const keys = Object.keys(m);
    // Keep the last 100 entries.
    if (keys.length > 100) keys.slice(0, keys.length - 100).forEach((k) => delete m[k]);
    sessionStorage.setItem(KEY, JSON.stringify(m));
  } catch { /* storage unavailable */ }
};

export default function ScrollMemory() {
  const location = useLocation();
  const type = useNavigationType();

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  // Record the position while the user scrolls this entry (reading it at the
  // moment of leaving is too late: the next page may already be shorter).
  useEffect(() => {
    const key = location.key;
    let t = null;
    let y = null;
    const flush = () => {
      t = null;
      if (y == null) return;
      const m = read(); m[key] = y; write(m);
    };
    const save = () => {
      y = window.scrollY; // read now, while this page is still on screen
      if (!t) t = setTimeout(flush, 150);
    };
    window.addEventListener('scroll', save, { passive: true });
    return () => { window.removeEventListener('scroll', save); if (t) { clearTimeout(t); flush(); } };
  }, [location.key]);

  useEffect(() => {
    if (type !== 'POP') {
      // A new page opens at the top. REPLACE is a page rewriting its own URL
      // (filters) — it stays where it is. A hash link scrolls to its anchor.
      if (type === 'PUSH' && !location.hash) window.scrollTo(0, 0);
      return undefined;
    }
    const y = read()[location.key];
    if (!y) return undefined;
    let stop = false;
    const cancel = () => { stop = true; };
    window.addEventListener('wheel', cancel, { passive: true, once: true });
    window.addEventListener('touchmove', cancel, { passive: true, once: true });
    window.addEventListener('keydown', cancel, { once: true });
    const started = Date.now();
    const tick = () => {
      if (stop) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max >= y - 4) { window.scrollTo(0, y); return; }
      if (Date.now() - started > 4000) { window.scrollTo(0, Math.max(0, max)); return; }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return () => {
      stop = true;
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchmove', cancel);
      window.removeEventListener('keydown', cancel);
    };
  }, [location.key, location.hash, type]);

  return null;
}
